import type { ApiResponse, SignupFormData } from '@shared/types'
import type { AxiosRequestConfig } from 'axios'
import type { RDomain, UpsertDomainPayload } from 'v2/pages/settings/types'
import type { GetService, PostService } from '..'

// Plivo CLI loopback-OAuth (PKCE) browser-login.
// See hodor's controllers/cliAuth for the full protocol.
export interface CLIExchangePayload {
  state: string
}
export interface RCLIExchangeResponse {
  code: string
  cb: string
}

export interface CLIRejectPayload {
  state: string
}
// `cb` is empty when the state has already expired or been redeemed: there is
// then no waiting CLI to redirect to, and the page just closes.
export interface RCLIRejectResponse {
  cb: string
}

export interface AuthService {
  login: PostService<RLoginResponse>
  loginv2: PostService<RLoginResponse>
  cliExchange: PostService<RCLIExchangeResponse, CLIExchangePayload>
  cliReject: PostService<RCLIRejectResponse, CLIRejectPayload>
  logout: GetService<void>
  // Invite User V2 endpoints
  inviteUserV2: PostService<void>
  activateV2: PostService<RLoginResponse, RActivatePayload>
  twoFactorAuth: PostService<TwoFactorAuthResponse, TwoFactorAuthPayload>
  twoFactorVerify: PostService<void, TwoFactorVerifyPayload>
  twoFactorResend: PostService<TwoFactorResendResponse, TwoFactorResendPayload>
  initiateSignup: PostService<void, InitiateSignupPayload>
  resendActivationEmail: PostService<void, ResendActivationEmailPayload>
  completeSignup: PostService<RCompleteSignupResponse, CompleteSignupPayload>
  completeInviteSignup: PostService<RLoginResponse, CompleteInviteSignupPayload>
  validateInviteSignupToken: GetService<RValidateInviteSignupTokenResponse, [token: string]>
  createOrganization: PostService<RLoginResponse, CreateOrganizationPayload>
  preSignupCheck: GetService<PreSignupCheckResponse, [tz: string, couponCode?: string]>
  // Internal support user endpoints
  inviteInternalUser: PostService<RInviteInternalUserResponse, RInviteInternalUserPayload>
  getSupportUserStatus: PostService<RSupportUserStatusResponse, RSupportUserStatusPayload>
  // Password V2 endpoints
  resetPasswordV2: PostService<void, RResetPasswordPayload>
  changePasswordV2: PostService<RUserChangePasswordPayload>
  forgotPasswordV2: PostService<void, RForgotPasswordPayload>
  validateResetTokenV2: PostService<void, RValidateResetTokenPayload>
  // Email change endpoints
  requestEmailChange: PostService<void, RRequestEmailChangePayload>
  verifyEmailChange: PostService<void, RVerifyEmailChangePayload>
}

export interface RRequestEmailChangePayload {
  region: string
  new_email: string
  method: string
  token: string
  session_uuid: string
}

export interface RVerifyEmailChangePayload {
  region: string
  otp: string
}

export interface RSsoAuthorizePayload {
  email?: string
  sso_type: string
  next?: string
  flow?: 'login' | 'signup'
  region?: string
  fingerprint_visitor_id?: string
  fingerprint_event_id?: string
}

export interface RSsoAuthorizeResponse {
  url?: string
}

export interface SsoRequirePayload {
  required: boolean
}

// hodor GET /accounts/sso-config. `active` implies `configured`. domain/identity_provider are
// omitempty on the wire (identity_provider is empty for a legacy row), so both stay optional.
export interface RSsoConfig {
  configured: boolean
  active: boolean
  domain?: string
  identity_provider?: string
  // When true, password/social login is blocked for this org (admins exempt).
  require_sso: boolean
}

// hodor POST /accounts/sso-config/setup.
export interface RSsoSetupResult {
  portal_link: string
}

export interface ChangePlanPayload {
  card_mapping_id: number
  new_plan: string
  old_plan: string
  change_type: 'upgrade' | 'downgrade'
  amount: number
  currency?: string
}

export interface ChangePlanResponse {
  message?: string
  success?: boolean
  recharge_status?: string
  plan_status?: string
  redirection_url?: string
}

export interface HodorService {
  getDomains: (orgUUID: string) => Promise<ApiResponse<RDomain>>
  upsertDomains: (
    orgUUID: string,
    data: UpsertDomainPayload,
  ) => Promise<ApiResponse<Record<string, unknown>>>
  verifyDomain: (token: string) => Promise<ApiResponse<Record<string, unknown>>>
  getSsoConfig: () => Promise<ApiResponse<RSsoConfig>>
  setupSso: () => Promise<ApiResponse<RSsoSetupResult>>
  // Both answer with data: null.
  setRequireSso: (data: SsoRequirePayload) => Promise<ApiResponse<null>>
  deleteSso: () => Promise<ApiResponse<null>>
  getFeatureFlags: GetService<RFeatureFlags>
  authorize: GetService<
    RSsoAuthorizeResponse,
    [
      {
        email?: string
        sso_type: string
        next?: string
        flow?: 'login' | 'signup'
        region?: string
      },
    ]
  >
  submitOutboundCallingIntake: PostService<
    Record<string, unknown>,
    {
      registration_country: string
      website: string
      use_case: string
      ip_country: string
      source: string
    }
  >
  getOutboundAccessStatus: GetService<ROutboundAccessStatus>
  changePlan: (data: ChangePlanPayload) => Promise<ApiResponse<ChangePlanResponse>>
  deactivateOrg: (data?: Record<string, unknown>) => Promise<ApiResponse<Record<string, unknown>>>
  getSubaccountUsersStatus: GetService<RSubaccountUsersStatus>
  addSubaccountUser: PostService<RSubaccountUser, SubaccountUserPayload>
  // 204 with no body.
  removeSubaccountUser: (id: string) => Promise<void>
  mintBuddyIdentityToken: () => Promise<ApiResponse<RBuddyIdentityToken>>
}

export interface RBuddyIdentityToken {
  token: string
  expires_at: string
}

// Uploader Service related types
export interface UploaderService {
  uploadFile: PostService<RFileUploadResponse, FormData, [AxiosRequestConfig | undefined]>
  fetchInvoiceDocument: (args: { file_path: string }) => Promise<Blob>
}

export interface RFileUploadResponse {
  file_id: string
  file_name: string
  file_size: number
  file_type: FileMimeType
  file_url: string
}

export enum FileMimeType {
  image = 'image',
  video = 'video',
  mp3 = 'audio/mpeg',
  document = 'document',
  image_jpeg = 'image/jpeg',
}

// API Response / Request related types
// Note: They should begin with R

export interface RLoginApiPayload {
  email: string
  password: string
  'g-recaptcha-response': string
  timezone: string
}

export enum UserRole {
  administrator = 'administrator',
  agent = 'agent',
  supervisor = 'supervisor',
  finance_analyst = 'finance_analyst',
  developer = 'developer',
  support = 'support',
  // Gated role, provisioned per-org; restricted to the UCC view like support-only
  ucc_reviewer = 'ucc_reviewer',
}

export interface RUserSession {
  aom_uuid: string
  auth_token: string
  browser_session_id: string
  organization_name: string
  // Stable identifier for the org, distinct from aom_uuid (AccountOrgMapping's own
  // uuid, not the Organization's). Optional: absent on sessions stored before this
  // field existed, and on backends that don't send it yet — callers must degrade to
  // organization_name matching when it's missing (see cli-authorize.tsx).
  organization_uuid?: string
  region: string
  roles: UserRole[]
  email: string
  name: string
}

// Interface for the feature flags used in the app
export interface RFeatureFlags {
  /**
   * Grants Custom SIP Platform to an account the Enterprise gate would otherwise
   * lock. The gate is new-signups-only (see `isEnterpriseLocked`), so this exists
   * for the case that gate cannot express: a post-cutoff signup on Free or
   * Professional that has been approved for its own SIP platform without moving
   * plan. Defaults to false, and only ever GRANTS access, never removes it.
   */
  customSipPlatformEnabled: boolean
  // Gates the Intercom onboarding messenger. Off in prod until it is switched on per-org
  // in PostHog; see use-intercom.
  onboardingAssistantEnabled: boolean
  multiChatApps: boolean
  isInitialData?: boolean
  chatDemoQuestions: boolean
  integrationApps: boolean
  slackTriggerEnabled: boolean
  vibeAgentDisabled: boolean
  isDebuggingMode: boolean
  mcpToolsInAiActionNodeEnabled: boolean
  playgroundEnabled: boolean
  channelVoiceEnabled: boolean
  // SER-5921: gates the self-serve enterprise-SSO settings tab (setup + Require SSO). Off by
  // default; granted per account via PostHog.
  ssoSelfServeEnabled: boolean
  isManualEvalEnabled: boolean
  isVibeEvalEnabled: boolean
  isSimulationEvalEnabled: boolean
  isBetaFeature: boolean
  isCashFreeAutoRecharge: boolean
  isCashFreeBankTransferEnabled: boolean
  // Internal testing override — bypasses the ₹10k netbanking floor when true.
  // Strictly defaults to false; hodor must explicitly grant it per account.
  isCashFreeTesting: boolean
  // SER-6024: shows the SIP Trunking tab on the Geo Permissions page while
  // the voice->zentrunk sync split is rolled out.
  sipTrunkingGeoPermissionsEnabled: boolean
  // SER-6086: playground transport swap — typed replies POST through hodor to the
  // runner and run events arrive over the runner's SSE stream instead of the
  // PubNub loop. Off by default (PubNub); granted via PostHog.
  playgroundStreamTransportEnabled: boolean
  isLivekitEnabled: boolean
  // Rollout gate for the v2.5 agent node (wire type agent_node, shown as
  // "Collect input"): shows the palette entry. ANDed with isLivekitEnabled —
  // the node is livekit-only.
  aiAgentV2_5Enabled: boolean
  // Rollout gate for the Stream node's palette entry. Off by default; the node
  // type itself stays registered either way, so pre-existing flows that contain
  // a stream node open and render regardless.
  streamNodeEnabled: boolean
  // AO Conversation Quality: when on for the org, the run-detail "View Conversation
  // Quality" panel is sourced from Agent Observability verdicts (richer than legacy)
  // and requests eval_source=ao. Off by default; granted per account via
  // PostHog → hodor `/feature-flags/evaluate`.
  aoConversationQualityEnabled: boolean
  usePlivoStunServer: boolean
  isVerifyWhatsAppEnabled: boolean
  usageReportsEnabled: boolean
  // Gates the Voice Agents report split (new multi-report layout). Off by default;
  // granted per org via PostHog -> hodor `/feature-flags/evaluate`.
  voiceAgentsSplitEnabled: boolean
  // voice-reporting (VR) insights kill-switch. When off, the entire VR card group in the usage
  // reports voice view (KPIs, hangup-source, suspected-issues, MOS distribution + the country MOS
  // column) is hidden so the grid reflows and the basic US-fed voice cards keep working. Off by
  // default; granted per account via PostHog -> hodor `/feature-flags/evaluate`.
  vrInsightsEnabled: boolean
  is140160NumberSeriesEnabled: boolean
  // Gates the UCC "Callback settings" sheet in the UCC dashboard. Off by default;
  // granted per account via PostHog -> hodor `/feature-flags/evaluate`.
  uccCallbacksEnabled: boolean
  // Kill switch for the "remove currency conversion" rollout: when on, displays
  // tagged with `skipConversion` render the backend-provided (already converted)
  // amount as-is instead of applying the frontend conversion rate.
  isCurrencyConversionDisabled: boolean
  // Gates Buddy's `/ticket` slash command, which bypasses the AI and raises a
  // support ticket (+ Slack escalation) directly. Defaults false; granted per
  // account via PostHog → hodor `/feature-flags/evaluate`.
  isBuddyDirectTicketEnabled: boolean
  // Swaps the aiassist Buddy panel for the chat widget running the Buddy flow. The panel
  // stays until the widget's app credentials are configured, so an early flip is harmless.
  buddyFlowChatEnabled: boolean
  isBuddyFeedbackDisabled: boolean
  // Gates the embedded Pylon "Support" portal (its route + bottom-pinned nav item). Off by
  // default; the embed only authenticates on prod, so hodor flips this on per-org once prod is
  // verified — letting the feature ship dark and open the gate without a redeploy.
  supportPortalEnabled: boolean
  // Gates the PSP-hosted (PCI-safe) card-entry flow: Stripe embedded Checkout +
  // Cashfree hosted modal, replacing the raw-card form where the PAN touches our UI.
  // Off by default; flipped on per-account via PostHog → hodor `/feature-flags/evaluate`
  // so the embedded flow can be verified in prod against test accounts while customers
  // stay on the legacy form. Backend dependency: payment-service Stripe checkout-session/
  // checkout-complete (PR #372) must be deployed before flipping on for Stripe accounts.
  isEmbeddedCardCheckoutEnabled: boolean
  // Hides the AI nodes (AI action, AI conversation, Skills) from the flow-builder
  // add-node palette when on. Off by default; flipped per-account via PostHog → hodor
  // `/feature-flags/evaluate` for orgs that should not author AI nodes.
  isAINodeDisabled: boolean
  // Per-org opt-in to the 10DLC CAMPAIGN_SVC ("api") transport: FE sends `X-TDLC-Transport: api`
  // on /tdlc/ calls. Off by default, flipped per-org via PostHog. hodor passes keys through raw,
  // so the PostHog key MUST be `tdlcApiTransportEnabled` verbatim. Fails closed to the SDK.
  tdlcApiTransportEnabled: boolean
  // Gates the retrieval-mode (Default/Docs) dropdown on the KB-create form. Off by default;
  // flipped on PER-ORG via PostHog `kbDocsModeEnabled` → hodor `/feature-flags/evaluate` (same
  // raw-key passthrough as above, so the PostHog key MUST be `kbDocsModeEnabled` verbatim), so docs
  // mode can be turned on for a single org (e.g. plivo) independently of the rag rollout.
  kbDocsModeEnabled: boolean
  // Gates the sandbox "Verified Destinations" tab on Phone Numbers. Trial accounts in
  // ANY market - sandbox replaces the free-trial credit grant everywhere, and India is
  // the primary market rather than the only one. Only the compliance comparison inside
  // the tab is India-only. Off by default until AMS assigns a sandbox DID at signup.
  // hodor passes PostHog keys through raw, so the key MUST be `isVoiceSandboxEnabled`
  // verbatim, camelCase and all.
  isVoiceSandboxEnabled: boolean
  // Gates the Business Details card on Organization settings — the read-only view
  // of an India account's CAF (Customer Application Form). Off by default and
  // granted per-org via PostHog -> hodor. Deliberately its own flag rather than
  // riding isVoiceSandboxEnabled: that one is scoped to free-trial accounts, which
  // are the accounts LEAST likely to hold a completed CAF. hodor passes PostHog
  // keys through raw, so the key MUST be `isIndiaCafEnabled` verbatim.
  isIndiaCafEnabled: boolean
  // Org-level rollout gate for the Voice Studio module (/voice-studio). Off by default;
  // targeted per account in PostHog -> hodor `/feature-flags/evaluate`. hodor passes PostHog
  // keys through raw, so the key MUST be `voiceStudioEnabled` verbatim.
  voiceStudioEnabled: boolean
  // Individual (PAN + DigiLocker) and business KYC for India. hodor and core read this PostHog flag
  // by org name and auth id: target orgs, as an email match shows UI that core refuses.
  isIndiaIndividualKycEnabled: boolean
  // Shows the agent subaccount tagging UI: the Sub account control on the agent
  // Settings tab, and the Sub Account columns and filters on the agent list and runs
  // pages. Off by default; flipped on per-org via PostHog `subaccountTaggingEnabled`
  // -> hodor `/feature-flags/evaluate` (raw-key passthrough, so the PostHog key must
  // match verbatim) as accounts are enrolled. Visibility only: tags are written by
  // the migration importer, and a flow's existing tag keeps riding every save while
  // the UI is hidden.
  subaccountTaggingEnabled: boolean
  // Org-level gate for scope tokens. PostHog allow-list, off by default.
  scopeTokensEnabled: boolean
  // Trust & Legal Compliance in Settings (admins only). Off by default, PostHog-targeted per org;
  // the PostHog key must be `trustAndLegalEnabled` verbatim. Each tab needs its own flag on top:
  // `complianceReportsEnabled` for Security Reports, `securityAddendumsEnabled` for Security Addendums.
  trustAndLegalEnabled: boolean
  complianceReportsEnabled: boolean
  securityAddendumsEnabled: boolean
  // Workspace sidebar (Voice Agents / Telephony switcher). Off by default, PostHog-targeted per
  // org; off renders the existing sidebar unchanged.
  workspaceSidebarEnabled: boolean
  // Shows Nimble voices in the agent voice picker. PostHog key must be `AgentRunnerNimbleEnabled` verbatim.
  AgentRunnerNimbleEnabled: boolean
}

export interface RUserChangePasswordPayload {
  new_password: string
  old_password: string
  region: string
}

export interface RLoginApiResponse {
  token: string
  user: { id: string; email: string; firstName: string; lastName: string }
}

export interface RResetPasswordPayload {
  password: string
  token: string
  'g-recaptcha-response': string
}

export interface RInviteUserPayload {
  email: string
  roles: string[]
}

export interface RActivatePayload {
  token: string
  region: string
  check_token: boolean
  'g-recaptcha-response': string
}

export interface RForgotPasswordPayload {
  email: string
  'g-recaptcha-response': string
}

export interface RValidateResetTokenPayload {
  token: string
  check_token: boolean
}

export interface ValidateSignupTokenPayload {
  token: string
}

export interface SignupResponse {
  aom_uuid: string
  org_id: number
  email: string
  message?: string
}

export interface TwoFactorAuthPayload {
  aom_uuid: string
  phone_number: string
  method: 'sms' | 'voice'
  region: string
  email: string
}

export interface TwoFactorAuthResponse {
  session_uuid: string
  aom_uuid: string
  message: string
  errors: string | string[] | null
  account_id: number
}

export interface TwoFactorVerifyPayload {
  session_uuid: string
  method: string
  aom_uuid: string
  account_id: number
  token: string
  is_signup: boolean
  region: string
}

// New structured payload interfaces for better type safety and clarity
export interface TwoFactorVerifyLoginPayload {
  session_uuid: string
  method: string
  aom_uuid: string
  account_id: number
  token: string
  is_authenticator: boolean
  verification_pending: boolean
  email: string
  originalLoginResponse: RLoginResponse
  region: string
}

export interface TwoFactorVerifySignupPayload {
  session_uuid: string
  method: string
  aom_uuid: string
  account_id: number
  token: string
  is_authenticator: boolean
  actor_email: string
  verification_pending: boolean
  region: string
}

export interface TwoFactorResendPayload {
  account_id: number
  method: 'sms' | 'voice'
  aom_uuid: string
  region: string
}

export interface TwoFactorResendResponse {
  message: string
  errors: string | string[] | null
  // session_uuid is only present when switching from authenticator to SMS mode
  session_uuid?: string
}

export interface GenerateFunctionSchemaParams {
  code: string
  function_name: string
  function_description: string
}

export interface GenerateFunctionSchemaResponse {
  function_schema: Record<string, unknown>
}

export interface GenerateCodeParams {
  code_instructions: string
  function_name: string
  function_description: string
}

export interface GenerateCodeResponse {
  code: string
  function_schema: Record<string, unknown>
  function_name: string
  function_description: string
}

export interface RAuthDetails {
  authId: string
  /** Omitted, together with the fields below, when `tokenVisible` is `false`. */
  authToken?: string
  newAuthToken?: string
  authTokenExpireTime?: string
  /**
   * Sent as `false` when the caller's profile lacks `api_credentials view` — the token
   * fields are then omitted entirely (authId is always returned). Absent means visible.
   */
  tokenVisible?: boolean
}

// Interface for login response that may require 2FA
export interface ROutboundAccessStatus {
  voice_cps_allowed: number
  inbound_cps_allowed: number
  zt_account_id?: string
  zt_cps_limit: number
  voice_outbound_enabled: boolean
  sip_outbound_enabled: boolean
  request_status: 'pending' | 'approved' | 'rejected' | 'hard_rejected' | ''
}

// The org's embed switch: false hides every sub-account user control.
export interface RSubaccountUsersStatus {
  enabled: boolean
}

export interface SubaccountUserPayload {
  sub_account: string
  email: string
  first_name: string
  last_name?: string
}

export interface RSubaccountUser {
  // The user's AOM uuid, which is also what DELETE takes.
  id: string
  email: string
  first_name: string
  last_name: string
  sub_account: string
  created_at: string
}

export interface RLoginResponse {
  // Present instead of a session when the user's org requires SSO (hodor HandleLoginV2): the
  // console redirects to sso_login_url and no other field is populated.
  sso_required?: boolean
  sso_login_url?: string
  authenticator_status: {
    is_default: boolean
    is_present: boolean
  }
  two_fa_enabled: boolean
  phone_number?: string
  session_uuid?: string
  account_id?: number
  aom_uuid: string
  auth_token: string
  browser_session_id: string
  organization_name: string
  // See RUserSession.organization_uuid — same field, optional for the same reason
  // (older backends / the org-switch and login endpoints may not send it yet).
  organization_uuid?: string
  region: string
  roles: UserRole[]
  email: string
  name: string
  redirect_two_fa_auth: boolean
}

export interface InitiateSignupPayload extends SignupFormData {
  coupon_code?: string
  timezone: {
    tzinfo: string
    title: string
  }
  fingerprint_visitor_id?: string
  fingerprint_event_id?: string
}

export interface ResendActivationEmailPayload {
  email: string
  'g-recaptcha-response': string
}

export interface CompleteSignupPayload {
  token: string
  email: string
  coupon_uuid?: string
}

export interface RCompleteSignupResponse {
  aom_uuid: string
  account_id: number
  org_id: number
  organization_name: string
  email: string
  name: string
  region: string
}

export interface CreateOrganizationPayload {
  organization_name: string
  region: string
  invitee_emails?: string[]
}

export interface PreSignupCheckResponse {
  allowed: boolean
  reason?: string
}

export interface RValidateInviteSignupTokenResponse {
  email: string
}

export interface CompleteInviteSignupPayload {
  token: string
  first_name: string
  last_name: string
  password: string
  region: string
  timezone: {
    tzinfo: string
    title: string
  }
}

export type RSupportUserAccessDuration = '1_week' | '1_month' | '3_months'

export interface RInviteInternalUserPayload {
  region: string
  duration?: RSupportUserAccessDuration
}

export interface RInviteInternalUserResponse {
  email: string
  expires_at: number
}

export interface RSupportUserStatusPayload {
  region: string
}

export interface RSupportUserStatusResponse {
  active: boolean
  expires_at: number | null
}
